import React, { useEffect, useState, useRef } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const TESTIMONIALS = [
  { n: "Aarav Mehta", c: "Adajan East, Surat", p: "Family Premium", r: 5, q: "From the moment we walked into the new Adajan hospital, the staff treated us like family. Lab tests, cardiology consult, follow-up — everything coordinated by one manager. We saved hours.", color: "1E4F87" },
  { n: "Priya Desai", c: "Pal, Surat", p: "Family Premium", r: 5, q: "I bought this for my parents and it is the best investment of the year. Annual full-body, dental, eye check-ups — all without a single phone-call chase. Truly care par excellence.", color: "A53A2A" },
  { n: "Rohit Iyer", c: "Honey Park, Adajan", p: "Family Premium", r: 5, q: "When my mother needed urgent cardiac care, the NPC desk had a senior cardiologist ready in under 20 minutes. No paperwork, no panic. Just care.", color: "0F2540" },
  { n: "Sneha Shah", c: "Palanpur Patia, Surat", p: "Individual", r: 5, q: "The diagnostics savings paid for the membership in two visits. The digital NPC card and dashboard feel like a premium private bank — but for healthcare.", color: "1F7A3D" },
  { n: "Vikram Trivedi", c: "Adajan West, Surat", p: "Family Premium", r: 5, q: "I have used five hospitals in Surat over the years. Nothing comes close to the priority access and concierge feel of the Nirmal Privilege Card. Our family will never go back.", color: "C89B3C" },
  { n: "Ananya Joshi", c: "Rander, Surat", p: "Family Premium", r: 5, q: "Every interaction has been thoughtful — from the way the manager called to confirm an appointment, to the WhatsApp updates. It feels designed for Indian families.", color: "7A2A1E" },
];

const avatar = (t) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(t.n)}&background=${t.color}&color=FFFFFF&bold=true&size=200&font-size=0.40`;

export default function TestimonialCarousel() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setI((v) => (v + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(id);
  }, [paused]);

  const t = TESTIMONIALS[i];

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonial-carousel"
    >
      {/* Decorative gradient blob */}
      <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[#A53A2A]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-72 h-72 rounded-full bg-[#1E4F87]/10 blur-3xl pointer-events-none" />

      <div className="relative rounded-[2rem] bg-white/85 backdrop-blur-xl border border-[#E1E8F0] p-8 lg:p-12 shadow-[0_30px_80px_-30px_rgba(15,37,64,0.25)]">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* Avatar + meta */}
          <div className="lg:col-span-4 flex flex-col items-start gap-5">
            <div className="relative">
              <img
                src={avatar(t)}
                alt={t.n}
                className="h-28 w-28 rounded-full ring-4 ring-white shadow-xl"
              />
              <div className="absolute -bottom-2 -right-2 bg-[#A53A2A] text-white text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full font-semibold">
                {t.p}
              </div>
            </div>
            <div>
              <div className="font-display text-2xl font-bold text-[#0F2540]">{t.n}</div>
              <div className="text-sm text-[#5C6B7A] mt-0.5">{t.c}</div>
              <div className="mt-2 flex gap-0.5">
                {Array.from({ length: t.r }).map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-[#C89B3C] text-[#C89B3C]" />
                ))}
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="lg:col-span-8 relative">
            <Quote className="absolute -top-2 -left-2 h-12 w-12 text-[#A53A2A]/15" />
            <blockquote className="font-display text-2xl lg:text-3xl text-[#0F2540] leading-snug relative z-10 pl-4">
              "{t.q}"
            </blockquote>
            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {TESTIMONIALS.map((_, k) => (
                  <button
                    key={k}
                    onClick={() => setI(k)}
                    aria-label={`Go to testimonial ${k + 1}`}
                    className={`h-2 rounded-full transition-all ${k === i ? "w-8 bg-[#1E4F87]" : "w-2 bg-[#E1E8F0]"}`}
                    data-testid={`carousel-dot-${k}`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setI((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                  className="h-10 w-10 rounded-full border border-[#E1E8F0] hover:border-[#1E4F87] grid place-items-center transition"
                  aria-label="Previous"
                  data-testid="carousel-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setI((v) => (v + 1) % TESTIMONIALS.length)}
                  className="h-10 w-10 rounded-full bg-[#1E4F87] text-white grid place-items-center hover:bg-[#0F3460] transition"
                  aria-label="Next"
                  data-testid="carousel-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
